import { useMemo, useState } from "react";
/**
 * Client-side column sorting for a dataset already in memory — suited to a bounded
 * report (e.g. a Chart of Accounts list). For a genuinely large General Ledger view,
 * §5's "server-side caching" note applies instead: fetch pre-sorted from the backend and
 * pass `sort`/`onSortChange` into `DataTable` as controlled props rather than using this
 * hook, so sorting doesn't require materializing the whole dataset client-side.
 */
export function useSortableData(rows, getValue, initial) {
    const [sort, setSort] = useState(initial ?? { key: null, direction: "asc" });
    const sorted = useMemo(() => {
        if (!sort.key)
            return rows;
        const key = sort.key;
        const factor = sort.direction === "asc" ? 1 : -1;
        return [...rows].sort((a, b) => {
            const av = getValue(a, key);
            const bv = getValue(b, key);
            if (av < bv)
                return -1 * factor;
            if (av > bv)
                return 1 * factor;
            return 0;
        });
    }, [rows, sort, getValue]);
    function requestSort(key) {
        setSort((prev) => {
            if (prev.key !== key)
                return { key, direction: "asc" };
            return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
        });
    }
    return { sortedData: sorted, sort, requestSort };
}
/**
 * Client-side substring filter across one or more string-producing fields — suited to a
 * quick "filter this page's rows" input. Structured/server-side filters (§5's aging
 * matrix, date-range filters, etc.) belong in the consuming app's query params, not this
 * hook.
 */
export function useTableFilter(rows, getSearchableText) {
    const [query, setQuery] = useState("");
    const filtered = useMemo(() => {
        if (query.trim().length === 0)
            return rows;
        const needle = query.trim().toLowerCase();
        return rows.filter((row) => getSearchableText(row).toLowerCase().includes(needle));
    }, [rows, query, getSearchableText]);
    return { filteredData: filtered, query, setQuery };
}
/**
 * Client-side pagination affordance. `DataTable` also accepts a fully-controlled
 * `pagination`/`onPageChange` prop pair for server-side pagination (§5's "infinite
 * scroll pagination" over a real GL, where the whole dataset should never be fetched
 * client-side) — use this hook only when the full dataset is already in memory.
 */
export function usePagination(rows, pageSize = 25) {
    const [page, setPage] = useState(1);
    const totalItems = rows.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const clampedPage = Math.min(page, totalPages);
    const pageData = useMemo(() => {
        const start = (clampedPage - 1) * pageSize;
        return rows.slice(start, start + pageSize);
    }, [rows, clampedPage, pageSize]);
    const state = { page: clampedPage, pageSize, totalItems, totalPages };
    return { pageData, pagination: state, setPage };
}
//# sourceMappingURL=hooks.js.map